import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Form, WeezHeader, ColorPickerLabel, Button, ToggleLabel, Message, Card, OptionalForm, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';

import { useForm, requiredField, emailField } from '../../utils/validators';
import { default as PartnerModel } from '../../models/partner';
import IdentityInvitation from '../../models/identityInvitation';
import PartnerMediaList from './PartnerMediaList';

import Loading from '../../components/loading';

const Partners = ({ organization, partnerId }) => {
    let history = useHistory();
    const [formState, setFormState, setFormObject, formErrors, formValidate] = useForm(
        {
            name: '',
            color: null,
            no_named: true
        },
        { name: requiredField }
    );
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [partner, setPartner] = React.useState(null);
    const [mailIdentityChanged, setIdentityChanged] = React.useState('');
    const [identity, setIdentity, setIdentityObject, formIdentityErrors, formIdentityValidate] = useForm({ name: null, email: null }, { email: emailField });
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (!partnerId) {
            setPartner(new PartnerModel());
            return setLoading(false);
        }
        PartnerModel.get({
            id: partnerId,
            organizationId: organization.id
        })
            .then(partner => {
                let formParams = {
                    name: partner.name,
                    no_named: partner.allow_no_named
                };
                if (partner.resolved_color) {
                    formParams['color'] = partner.resolved_color;
                }
                setFormObject(formParams, true);
                setIdentity('name', partner.name);
                setPartner(partner);
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.partners.get.error'));
            })
            .finally(() => {
                setLoading(false);
            });

        IdentityInvitation.getAll({ organizationId: organization.id, partnerId })
            .then(response => {
                setIdentityObject({ id: response.id, inherited: response.inherited, name: response.reply_to.name, email: response.reply_to.email });
                setIdentityChanged(response.reply_to.email);
            })
            .catch(e => {
                // No identity on load because organization do not have one, and we do not have create one before
            });
    }, [partnerId, organization, setIdentityChanged, setIdentityObject, setPartner]);

    const handleSubmit = React.useCallback(() => {
        if (!formValidate() || !formIdentityValidate()) {
            return;
        }
        partner.name = formState.name;
        partner.allow_no_named = formState.no_named;
        if (partner.resolved_color !== formState.color) {
            partner.color = formState.color;
        }
        partner
            .save({ organizationId: organization.id })
            .then(({ response }) => {
                if (identity.email) {
                    if (identity.email !== mailIdentityChanged || !identity.inherited) {
                        // Save identity only if mail have change or identity is not herited
                        let _identity = new IdentityInvitation(identity);
                        if (_identity.inherited) {
                            // If identity was inherited from another level (orga or default)
                            delete _identity.id;
                        }
                        _identity['from_address'] = { name: formState.name, email: 'invitation@weezevent.com' };
                        _identity['reply_to'] = { name: formState.name, email: identity.email };
                        // No then case because we do not want toast or anything else.
                        _identity.save({ organizationId: organization.id, partnerId: response.uuid }).catch(e => {
                            console.warn(e);
                            toast.error(t('ticket.partners.settings.identity.create_update_error'));
                        });
                    }
                }

                if (partnerId) {
                    toast.success(t('ticket.partners.modify.success'));
                    history.push(`/ticket/O${organization.id}/partners/${response.uuid}/batches`);
                } else {
                    toast.success(t('ticket.partners.save.success'));
                    history.push(`/ticket/O${organization.id}/partners/${response.uuid}/batches/new`);
                }
            })
            .catch(e => {
                console.error(e);
                if (partnerId) {
                    toast.success(t('ticket.partners.modify.error'));
                } else {
                    toast.error(t('ticket.partners.save.error'));
                }
            });
    }, [partner, formState, organization, history, toast, identity, setIdentityObject, partnerId, mailIdentityChanged]);

    if (loading) {
        return <Loading />;
    }

    return (
        <React.Fragment>
            <WeezHeader
                title={partner.name ?? t('ticket.partners.title')}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/partners`) }}
                rightComponent={[<Button primary key="primary" disabled={partner?.isDeleted()} label={t('ticket.common.action.save')} onClick={handleSubmit} />]}
            />
            <Form>
                <Card.FormManager useSteps={false}>
                    <Card.FormStep
                        title={t('ticket.partners.informations.title')}
                        rightComponent={
                            <Message type="tips" title={t('ticket.common.tips')} mini>
                                {t('ticket.partners.informations.tips')}
                            </Message>
                        }
                    >
                        <>
                            <Form.Field error={formErrors['name'] ? true : false}>
                                <Form.Label required label={t('ticket.partners.informations.name.label')} />
                                <Form.Sublabel label={t('ticket.partners.informations.name.sublabel')} />
                                {/* FIXME: use v.value when faulty "0" instead of "empty" value is fixed @nacre */}
                                <Form.Input
                                    disabled={partner?.isDeleted()}
                                    required={formErrors['name'] ? true : false}
                                    value={formState.name}
                                    onChange={({ target: { value } }) => {
                                        setFormState('name', value);
                                        setIdentity('name', value);
                                    }}
                                />
                                <Form.FieldErrors errors={formErrors['name'] ? [t(formErrors['name'])] : ['']} />
                            </Form.Field>
                            <Form.Field error={formIdentityErrors['email'] ? true : false}>
                                <Form.Label label={t('ticket.partners.informations.email.label')} />
                                <Form.Sublabel label={t('ticket.partners.informations.email.sublabel')} />
                                <Form.Input
                                    disabled={partner?.isDeleted()}
                                    required={formIdentityErrors['email'] ? true : false}
                                    value={identity.email}
                                    onChange={({ target: { value } }) => {
                                        setIdentity('email', value);
                                    }}
                                />
                                <Form.FieldErrors errors={formIdentityErrors['email'] ? [t(formIdentityErrors['email'])] : ['']} />
                            </Form.Field>
                        </>
                    </Card.FormStep>
                    <OptionalForm labelOpen={<span>{t(`ticket.partners.options.show`)}</span>} labelClosed={<span>{t(`ticket.partners.options.hide`)}</span>} displayAdditional>
                        <Card.FormStep
                            title={t('ticket.partners.customize')}
                            rightComponent={
                                <Message type="tips" title={t('ticket.common.tips')} mini>
                                    {t('ticket.partners.options.tips')}
                                </Message>
                            }
                        >
                            <>
                                <Form.Field>
                                    <ColorPickerLabel
                                        label={t('ticket.partners.color.label')}
                                        onChange={(event, value) => {
                                            setFormState('color', value);
                                        }}
                                        value={formState.color || '#FFF'}
                                    />
                                </Form.Field>
                                {partnerId && <PartnerMediaList organization={organization} partnerId={partnerId} />}
                            </>
                        </Card.FormStep>
                        <Card.FormStep title={t('ticket.partners.display_settings.title')} rightComponent={<div></div>}>
                            <Form.Field>
                                <ToggleLabel
                                    inlinePosition="left"
                                    label={t('ticket.partners.display_settings.no-named')}
                                    onChange={() => setFormState('no_named', !formState.no_named)}
                                    checked={formState.no_named}
                                />
                            </Form.Field>
                        </Card.FormStep>
                    </OptionalForm>
                </Card.FormManager>
            </Form>
        </React.Fragment>
    );
};

export default Partners;
Partners.propTypes = {
    organization: PropTypes.object.isRequired,
    partnerId: PropTypes.string
};
